import React, { Fragment } from "react";

import {
  Box,
  CircularProgress,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Check, Clear as Cross } from "@material-ui/icons";
import { Controller } from "react-hook-form/dist/react-hook-form.ie11";
import { green } from "@material-ui/core/colors";
import clsx from "clsx";

import { AccessCodeProps } from "./types";
import { FormHeader, MaskedTextField, Page } from "@hubcapp/components";
import Button from "@ds-developer/button";

const useStyles = makeStyles({
  resendContainer: {
    display: "flex",
    alignItems: "center",
  },
  resendIcon: {
    fontSize: 18,
  },
  resendSuccessIcon: {
    color: green[500],
  },
});

const AccessCode: React.FC<AccessCodeProps> = ({
  control,
  errors,
  isLoading = false,
  isValid,
  onSubmit,
  primaryErrorMessage,
  resendLoading = false,
  resendStatus,
  handleDigitCodeEvent,
  onResendClicked,
}) => {
  const classes = useStyles();

  return (
    <Page title="Enter access code">
      <form onSubmit={onSubmit}>
        <FormHeader
          title="Enter access code"
          subtitle={
            primaryErrorMessage ? (
              primaryErrorMessage
            ) : (
              <Fragment>
                <span>
                  We have sent an access code to the mobile number on record.
                </span>
                <br />
                <br />
                <span>Check your device and enter the 6-digit code here.</span>
              </Fragment>
            )
          }
          error={Boolean(primaryErrorMessage)}
        />
        {/* This needs to use the Controller syntax due to the text masking */}
        <Controller
          as={MaskedTextField}
          autoComplete="one-time-code"
          control={control}
          error={Boolean(errors?.accessCode)}
          helperText={errors?.accessCode?.message}
          helperTextProps={{
            ["data-test"]: "access-code-helper-text",
          }}
          inputProps={{
            ["data-test"]: "access-code-input",
          }}
          mask={[
            /[0-9]/,
            /[0-9]/,
            /[0-9]/,
            " ",
            "-",
            " ",
            /[0-9]/,
            /[0-9]/,
            /[0-9]/,
          ]}
          name="accessCode"
          placeholder="6-digit code"
          onClick={() => handleDigitCodeEvent()}
        />
        <Box marginTop={3} marginBottom={2}>
          <Button
            type="submit"
            color="primary"
            size="large"
            variant="contained"
            disabled={!isValid}
            loading={isLoading}
            data-test="submit-button"
          >
            Verify
          </Button>
        </Box>
        <div className={classes.resendContainer}>
          <Typography color="textSecondary" variant="body1">
            Didn’t receive the code?
          </Typography>
          <Button
            color="primary"
            size="large"
            disabled={resendLoading || resendStatus === "SUCCESS"}
            data-test="resend-button"
            onClick={onResendClicked}
          >
            Resend it
          </Button>
          {resendLoading && (
            <CircularProgress size={15} className={classes.resendIcon} />
          )}
          {!resendLoading && resendStatus === "SUCCESS" && (
            <Check
              className={clsx(classes.resendIcon, classes.resendSuccessIcon)}
            />
          )}
          {!resendLoading && resendStatus === "FAILURE" && (
            <Cross color="error" className={classes.resendIcon} />
          )}
        </div>
      </form>
    </Page>
  );
};

export default AccessCode;
